import type React from 'react';
import { useContext } from 'react';

import type { RoleRequirementsEvent } from '../../analytics';
import type { RecommendedQuestionGraphQL } from '../../graphql/type';
import {
  type JobDetails,
  type RecommendedQuestion,
  type SelectedQuestion,
  SelectionTypeView,
  WorkType,
} from '../../sdk';
import type { AnswerChangeHandler } from '../AnswerList/AnswerList';
import {
  type State,
  addSearchQuestion,
  getQuestions,
  isAnswerSelected,
  isQuestionSelected,
  selectedAnswerRange,
  toggleSelectedAnswer,
  toggleSelectedQuestion,
  toggleSelectedEssentialQuestion,
  type Actions,
  isEssentialSelected,
  getSelectionTypeView,
  setSelectionTypeView,
  type StatefulRecommendedQuestion,
} from '../App/state';
import LoaderPanel from '../LoaderPanel/LoaderPanel';
import QuestionList from '../QuestionList/QuestionList';
import { AnalyticsContext } from '../RoleRequirements/RoleRequirements';

interface Props {
  questionnaireId: string;
  isLoading: boolean;
  onChange?: (selections: SelectedQuestion[]) => void;
  onEvent?: (event: RoleRequirementsEvent) => void;
  state: State;
  dispatch: React.Dispatch<Actions>;
  setDisplayValidation: (display: boolean) => void;
  displayValidation: boolean;
  showReviewQuestions: boolean;
  isVerifyBoosted: boolean;
  setIsVerifyBoosted: (isVerifyBoosted: boolean) => void;
  verifyBoostError: boolean;
  setVerifyBoostError: (error: boolean) => void;
}

const Questions: React.FC<Props> = ({
  isLoading,
  state,
  dispatch,
  setDisplayValidation,
  displayValidation,
  showReviewQuestions,
  questionnaireId,
  isVerifyBoosted,
  setIsVerifyBoosted,
  verifyBoostError,
  setVerifyBoostError,
}) => {
  const questions: StatefulRecommendedQuestion[] = getQuestions(state);
  const analytics = useContext(AnalyticsContext);

  const toggleAnswerSelection: AnswerChangeHandler = (answer) => {
    const { questionId, answerId } = answer;
    const isSelectingAnswer = !isAnswerSelected(state)(questionId, answerId);

    analytics.sendAnswerSelectionEvent(
      answerId,
      questionId,
      questionnaireId,
      isSelectingAnswer,
      state.firstInteraction,
    );

    if ('type' in answer) {
      dispatch(selectedAnswerRange(questionId, answerId, answer.type));
    } else {
      dispatch(toggleSelectedAnswer(questionId, answerId));
    }
  };

  const toggleQuestionSelection = (questionId: string) => {
    const isSelectingQuestion = !isQuestionSelected(state)(questionId);

    analytics.sendQuestionSelectionEvent(
      questionId,
      questionnaireId,
      isSelectingQuestion,
      state.firstInteraction,
    );
    dispatch(toggleSelectedQuestion(questionId));
    setDisplayValidation(false);
  };

  const toggleEssentialQuestionSelection = (questionId: string) => {
    const newEssentialToggleState = !isEssentialSelected(state)(questionId);

    analytics.sendEssentialSelectionEvent(
      questionId,
      questionnaireId,
      newEssentialToggleState,
      state.firstInteraction,
    );

    dispatch(toggleSelectedEssentialQuestion(questionId));
  };

  const toggleSelectionTypeView = (questionId: string) => {
    const newDisplayedAnswerType =
      getSelectionTypeView(state)(questionId) === SelectionTypeView.Answers
        ? SelectionTypeView.SimplifiedAnswers
        : SelectionTypeView.Answers;

    dispatch(setSelectionTypeView(questionId, newDisplayedAnswerType));
  };

  const handleSearchQuestion = (
    question: RecommendedQuestion | RecommendedQuestionGraphQL,
  ) => {
    const questionIsAlreadySelected = isQuestionSelected(state)(question.id);

    if (questionIsAlreadySelected) {
      return;
    }

    analytics.sendQuestionSelectionEvent(
      question.id,
      questionnaireId,
      true,
      state.firstInteraction,
    );

    dispatch(addSearchQuestion(question));
  };

  if (questions.length > 0) {
    return (
      <QuestionList
        showCertsyTooltip={state.settings.showCertsyTooltip ?? false}
        questions={questions}
        selectionLimit={state.selectionLimit}
        toggleAnswerSelection={toggleAnswerSelection}
        addQuestion={handleSearchQuestion}
        toggleQuestionSelection={toggleQuestionSelection}
        toggleEssentialQuestionSelection={toggleEssentialQuestionSelection}
        toggleSelectionTypeView={toggleSelectionTypeView}
        settings={state.settings}
        displayValidation={displayValidation}
        showReviewQuestions={showReviewQuestions}
        questionnaireId={questionnaireId}
        bundlingQuestionCategory={state.bundlingQuestionCategory}
        isVerifyBoosted={isVerifyBoosted}
        setIsVerifyBoosted={setIsVerifyBoosted}
        verifyBoostError={verifyBoostError}
        setVerifyBoostError={setVerifyBoostError}
      />
    );
  }

  return <LoaderPanel isLoading={isLoading} />;
};

Questions.displayName = 'Questions';

export { Questions, WorkType, type JobDetails };
